import 'Components/vm-button/vm-button';
import 'Components/vm-icon/vm-icon';
import 'Components/vm-content-popper/vm-content-popper';
import 'Components/vm-popover/vm-popover';
import 'Components/vm-dropdown/vm-dropdown';
import 'Components/vm-dropdown/vm-dropdown-item';
import * as Sentry from '@sentry/browser';
import { TanStackFormController } from '@tanstack/lit-form';
import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { addPlaylistItem, createPlaylist, fetchUserPlaylists } from 'Api/playlists';
import { bookmarkVideo, removeBookmarkVideo } from 'Api/videos';
import { ApiFetchError } from 'Utils/api-fetch-request';
import Styles from './vm-video-save-menu.scss';

interface NewPlaylistFormValues {
    title: string;
    description: string;
}

export interface PlaylistOption {
    id: number;
    slug: string;
    title: string;
}

/**
 * Save-to-playlist menu for a video. Renders a "Save" trigger button that
 * opens a popover containing:
 *
 * - A favorite/bookmark toggle that hits `POST/DELETE /api/videos/<slug>/bookmark/`.
 * - The user's existing playlists; clicking one adds the current video via
 *   `POST /api/playlist-items/`.
 * - A "Create new playlist" entry that opens a content popper (modal on
 *   desktop, bottom sheet on mobile) with a TanStack Form for title +
 *   description. On submit, the playlist is created and the current video
 *   is added to it in a chained two-step API call.
 *
 * Initial playlist list is supplied via the `initialPlaylists` property,
 * typically populated by the parent `vm-video-actions` after hydrating
 * server-rendered seed divs. After a new playlist is created, the list is
 * refreshed in the background via `GET /api/playlists/?author=<uuid>` so
 * the dropdown reflects server state.
 *
 * Optimistic updates: newly-created playlists are appended to the dropdown
 * immediately, before the refresh completes. Adding to an existing playlist
 * is also optimistic — UI updates immediately and reverts via toast on
 * failure (no inline-state rollback needed since clicking just dispatches
 * an API call; no per-playlist UI state is mutated).
 *
 * Mixpanel: fires `VideoAddedToPlaylist` on successful save (with
 * `playlist_id: number`), and `VideoFavorited` with `favorited: boolean` on
 * bookmark toggle. Reads context from `window.__VUMEDI_VIDEO_DETAIL_CONTEXT__`;
 * events skip silently if the context is missing.
 *
 * Toasts: dispatches `vm-toast-show` events on `document` for success/error
 * notifications. Relies on a globally-mounted `<vm-toast-container>`.
 *
 * @example
 * ```html
 * <vm-video-save-menu
 *   video-slug="some-video-slug"
 *   user-profile-uuid="abc-123"
 *   user-has-bookmarked
 * ></vm-video-save-menu>
 * ```
 *
 * `initialPlaylists` is a property (not an attribute), so it's set via
 * property binding from the parent:
 *
 * ```ts
 * html`<vm-video-save-menu .initialPlaylists=${list} ...></vm-video-save-menu>`
 * ```
 */
@customElement('vm-video-save-menu')
export class VmVideoSaveMenu extends LitElement {
    static override styles = Styles;

    /** Slug of the video being acted upon. Used as the `content` field in playlist-item API calls. */
    @property({ type: String, attribute: 'video-slug' })
    videoSlug = '';

    /**
     * Current user's profile UUID. Required for the background playlist
     * refresh (`?author=<uuid>`); the refresh is a no-op when empty.
     */
    @property({ type: String, attribute: 'user-profile-uuid' })
    userProfileUuid = '';

    /** Whether the current user has bookmarked this video. Toggles the label and POST/DELETE. */
    @property({ type: Boolean, attribute: 'user-has-bookmarked' })
    userHasBookmarked = false;

    /**
     * Whether the current user has already saved this video to one of their
     * playlists (excluding favorites). Combined with `userHasBookmarked` to
     * decide whether the trigger reads "Save" or "Saved". Flipped to `true`
     * after a successful playlist add inside the menu.
     */
    @property({ type: Boolean, attribute: 'user-has-saved-to-playlist' })
    userHasSavedToPlaylist = false;

    /**
     * Initial playlist list. Set once (via property binding) by the parent
     * `vm-video-actions` from server-rendered seed elements. After this
     * one-time hydration, the component owns `_playlists` state internally;
     * subsequent changes to `initialPlaylists` are ignored.
     */
    @property({ attribute: false })
    initialPlaylists: PlaylistOption[] = [];

    @state()
    private _popperOpen = false;

    @state()
    private _playlists: PlaylistOption[] = [];

    @state()
    private _bookmarkPending = false;

    private _hydratedFromInitial = false;

    private _form = new TanStackFormController(this, {
        defaultValues: { title: '', description: '' } satisfies NewPlaylistFormValues,
        onSubmit: async ({ value, formApi }) => {
            await this._submitNewPlaylist(value);
            formApi.reset();
        },
    });

    override connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this._onPlaylistItemClick);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onPlaylistItemClick);
    }

    override willUpdate(changed: PropertyValues<this>) {
        if (!this._hydratedFromInitial && changed.has('initialPlaylists') && this.initialPlaylists.length > 0) {
            this._playlists = [...this.initialPlaylists];
            this._hydratedFromInitial = true;
        }
    }

    private _onPlaylistItemClick = (e: Event) => {
        const item = e
            .composedPath()
            .find((n): n is HTMLElement => n instanceof HTMLElement && !!n.dataset.playlistSlug);
        if (!item) return;
        const slug = item.dataset.playlistSlug ?? '';
        const id = parseInt(item.dataset.playlistId ?? '', 10);
        if (slug) this._addToPlaylist(slug, id);
    };

    private _onCreatePlaylistClick = () => {
        this._popperOpen = true;
    };

    private _handleFormCancel = () => {
        this._popperOpen = false;
    };

    private async _submitNewPlaylist({ title, description }: NewPlaylistFormValues) {
        let playlist;
        try {
            playlist = await createPlaylist({ title: title.trim(), description: description.trim() });
        } catch {
            this._showToast({ message: 'Could not create playlist.', variant: 'error' });
            return;
        }

        // Optimistic append so the new playlist shows up immediately;
        this._playlists = [...this._playlists, { id: playlist.id, slug: playlist.slug, title: playlist.title }];
        this._refreshPlaylists();

        try {
            await addPlaylistItem({ playlistSlug: playlist.slug, contentSlug: this.videoSlug });
            this.userHasSavedToPlaylist = true;
            this._trackVideoEvent('VideoAddedToPlaylist', { playlist_id: playlist.id });
            this._showToast({
                message: `Saved to ${playlist.title}`,
                variant: 'confirmation',
                icon: 'check-circle-solid',
            });
            this._popperOpen = false;
        } catch {
            this._showToast({
                message: `Playlist created, but couldn't add this video.`,
                variant: 'error',
            });
        }
    }

    private _onBookmarkToggle = async () => {
        if (this._bookmarkPending || !this.videoSlug) return;

        const wasBookmarked = this.userHasBookmarked;
        this.userHasBookmarked = !wasBookmarked;
        this._bookmarkPending = true;

        try {
            if (wasBookmarked) {
                await removeBookmarkVideo(this.videoSlug);
            } else {
                await bookmarkVideo(this.videoSlug);
            }
            this._trackVideoEvent('VideoFavorited', { favorited: !wasBookmarked });
            this._showToast({
                message: wasBookmarked ? 'Removed from favorites.' : 'Saved to favorites.',
                variant: 'confirmation',
                icon: 'check-circle-solid',
            });
        } catch (e) {
            this.userHasBookmarked = wasBookmarked;

            if (!(e instanceof ApiFetchError)) {
                Sentry.captureException(e, {
                    extra: {
                        videoSlug: this.videoSlug,
                        action: wasBookmarked ? 'unbookmark' : 'bookmark',
                    },
                });
            }

            this._showToast({
                message: wasBookmarked ? 'Could not remove from favorites.' : 'Could not add to favorites.',
                variant: 'error',
            });
        } finally {
            this._bookmarkPending = false;
        }
    };

    private async _addToPlaylist(playlistSlug: string, playlistId: number) {
        try {
            await addPlaylistItem({ playlistSlug, contentSlug: this.videoSlug });
            this.userHasSavedToPlaylist = true;
            this._trackVideoEvent('VideoAddedToPlaylist', { playlist_id: playlistId });
            const playlist = this._playlists.find((p) => p.slug === playlistSlug);
            const message = playlist ? `Saved to ${playlist.title}` : 'Added to playlist.';
            this._showToast({ message, variant: 'confirmation', icon: 'check-circle-solid' });
        } catch {
            this._showToast({ message: 'Could not add to playlist.', variant: 'error' });
        }
    }

    private async _refreshPlaylists() {
        if (!this.userProfileUuid) return;
        try {
            const data = await fetchUserPlaylists({ userProfileUuid: this.userProfileUuid });
            this._playlists = data.results
                .filter((p) => !p.OR)
                .map((p) => ({ id: p.id, slug: p.slug, title: p.title }));
        } catch (e) {
            if (!(e instanceof ApiFetchError)) {
                Sentry.captureException(e, {
                    extra: { userProfileUuid: this.userProfileUuid },
                });
            }
        }
    }

    private _trackVideoEvent(eventName: string, extra: Record<string, unknown> = {}) {
        const ctx = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;
        if (!ctx?.videoId) return;
        window.vumedi?.mixpanel?.track(eventName, {
            video_id: ctx.videoId,
            video_slug: ctx.videoSlug,
            channel_id: ctx.channelId,
            template_version: ctx.templateVersion,
            ...extra,
        });
    }

    private _showToast(detail: { message: string; variant?: 'confirmation' | 'error'; icon?: string }) {
        document.dispatchEvent(new CustomEvent('vm-toast-show', { detail }));
    }

    private _fieldClass(field: { state: { meta: { errors: unknown[] } } }) {
        return classMap({ 'has-error': field.state.meta.errors.length > 0 });
    }

    override render() {
        return html`
            <vm-popover>
                <vm-button variant="solid" color-scheme="secondary" size="sm" slot="trigger">
                    <vm-icon
                        slot="icon-trailing"
                        name=${this.userHasBookmarked || this.userHasSavedToPlaylist
                            ? 'check-circle-solid'
                            : 'chevron-down'}
                        size="sm"
                    ></vm-icon>
                    ${this.userHasBookmarked || this.userHasSavedToPlaylist ? 'Saved' : 'Save'}
                </vm-button>
                <vm-dropdown>
                    <vm-dropdown-item group>
                        Save to
                        <vm-dropdown-item groupitem slot="children" @click=${this._onBookmarkToggle}>
                            ${this.userHasBookmarked ? 'Remove from Favorites' : 'Add to Favorites'}
                        </vm-dropdown-item>
                        ${this._playlists.map(
                            (p) =>
                                html`<vm-dropdown-item
                                    groupitem
                                    slot="children"
                                    data-playlist-slug=${p.slug}
                                    data-playlist-id=${p.id}
                                    >${p.title}</vm-dropdown-item
                                >`,
                        )}
                    </vm-dropdown-item>
                    <vm-dropdown-item @click=${this._onCreatePlaylistClick}>
                        <vm-icon slot="icon-leading" name="save"></vm-icon>
                        Create new playlist
                    </vm-dropdown-item>
                </vm-dropdown>
            </vm-popover>

            <vm-content-popper ?open=${this._popperOpen} @vm-close=${this._handleFormCancel}>
                <span slot="header" class="vm-video-save-menu__form-header">New Playlist</span>
                <form
                    class="vm-video-save-menu__form"
                    @submit=${(e: Event) => {
                        e.preventDefault();
                        this._form.api.handleSubmit();
                    }}
                >
                    ${this._form.field(
                        {
                            name: 'title',
                            validators: {
                                onSubmit: ({ value }) => (!value.trim() ? 'Title is required' : undefined),
                            },
                        },
                        (field) => html`
                            <label class="vm-video-save-menu__form-label">
                                <span class="vm-video-save-menu__form-label-text">Title</span>
                                <input
                                    type="text"
                                    name="title"
                                    class=${this._fieldClass(field)}
                                    placeholder="Title"
                                    .value=${field.state.value}
                                    @input=${(e: Event) => field.handleChange((e.target as HTMLInputElement).value)}
                                    @blur=${() => field.handleBlur()}
                                />
                                ${field.state.meta.errors.length
                                    ? html`<span class="vm-video-save-menu__form-error" role="alert">
                                          ${field.state.meta.errors[0]}
                                      </span>`
                                    : nothing}
                            </label>
                        `,
                    )}
                    ${this._form.field(
                        { name: 'description' },
                        (field) => html`
                            <label class="vm-video-save-menu__form-label">
                                <span class="vm-video-save-menu__form-label-text">Description</span>
                                <textarea
                                    name="description"
                                    placeholder="Description"
                                    rows="3"
                                    .value=${field.state.value}
                                    @input=${(e: Event) => field.handleChange((e.target as HTMLTextAreaElement).value)}
                                    @blur=${() => field.handleBlur()}
                                ></textarea>
                            </label>
                        `,
                    )}
                    <div class="vm-video-save-menu__form-actions">
                        <vm-button
                            variant="solid"
                            color-scheme="primary"
                            size="sm"
                            type="submit"
                            ?loading=${this._form.api.state.isSubmitting}
                        >
                            Save
                        </vm-button>
                        <vm-button
                            variant="outline"
                            color-scheme="neutral"
                            size="sm"
                            ?disabled=${this._form.api.state.isSubmitting}
                            @click=${this._handleFormCancel}
                        >
                            Cancel
                        </vm-button>
                    </div>
                </form>
            </vm-content-popper>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-video-save-menu': VmVideoSaveMenu;
    }
}
